import { useState, useEffect } from 'react'
import { useSectionsStore } from './store'

// DOM-based section titles using direct subscription
export function SectionTitles() {
  // Use React state for the component
  const [state, setState] = useState({
    sections: useSectionsStore.getState().sections,
    sectionDescriptions: useSectionsStore.getState().sectionDescriptions,
    activeSection: useSectionsStore.getState().activeSection,
    currentIndex: useSectionsStore.getState().currentIndex, // Get from store instead of tracking locally
    showSectionTitles: useSectionsStore.getState().showSectionTitles, // Add showSectionTitles to state
    shouldHideTitlesDuringTransition: useSectionsStore.getState().shouldHideTitlesDuringTransition
  })
  
  // Debug logging for component lifecycle
  // console.log('SectionTitles component mounted/rendered - showSectionTitles:', state.showSectionTitles);
  
  // Connect to store with useEffect
  useEffect(() => {
    console.log('SectionTitles useEffect - setting up store subscription');
    
    // This function will run on every store update
    const unsubscribe = useSectionsStore.subscribe(newState => {
      setState(prevState => ({
        ...prevState,
        sections: newState.sections,
        sectionDescriptions: newState.sectionDescriptions,
        activeSection: newState.activeSection,
        currentIndex: newState.currentIndex, // Update from store's current index
        showSectionTitles: newState.showSectionTitles, // Subscribe to showSectionTitles changes
        shouldHideTitlesDuringTransition: newState.shouldHideTitlesDuringTransition
      }))
    })
    
    // Clean up subscription when component unmounts
    return () => {
      console.log('SectionTitles component unmounting - cleaning up subscription');
      unsubscribe();
    }
  }, [])
  
  // Format description text to highlight the separator
  const formatDescription = (text) => {
    if (!text) return null;
    
    return text.split('|').map((segment, index, array) => (
      <span key={index}>
        {segment.trim()}
        {index < array.length - 1 && <span className="separator">|</span>}
      </span>
    ));
  };

  // Function to determine if a section should be visible
  const shouldShowSection = (section) => {
    // Show section only if it's active AND current index has reached section's startIndex exactly
    // AND showSectionTitles is true
    const result = state.showSectionTitles && section.id === state.activeSection && state.currentIndex >= section.startIndex;
    if (result) {
      // console.log(`Section ${section.id} should show: showSectionTitles=${state.showSectionTitles}, activeSection=${state.activeSection}, currentIndex=${state.currentIndex}, startIndex=${section.startIndex}`);
    }
    return result;
  };
  
  // Function to get opacity for titles (fade during transitions instead of hiding)
  const getTitleOpacity = (section) => {
    // Check if titles should be hidden during transition (Studio viewed + video scaling)
    const hideDuringTransition = state.shouldHideTitlesDuringTransition();
    
    if (hideDuringTransition) {
      // console.log(`Fading section ${section.id} during video scaling transition`);
      return 0; // Fade to transparent
    }
    
    // Normal visibility logic
    return shouldShowSection(section) ? 1 : 0;
  };
  
  // Always render, but control opacity for smooth fading
  const hideDuringTransition = state.shouldHideTitlesDuringTransition();
  if (!state.showSectionTitles && !hideDuringTransition) {
    console.log('SectionTitles: Not rendering because showSectionTitles is false');
    return null;
  }
  
  return (
    <>
      {/* Description container at the top */}
      <div className="section-descriptions-container">
        {state.sections.map(section => (
          <div 
            key={`desc-${section.id}`}
            className={`section-description ${shouldShowSection(section) ? 'active' : ''}`}
            style={{ 
              opacity: getTitleOpacity(section),
              transform: shouldShowSection(section) ? 'translateY(0)' : 'translateY(10px)',
              transition: 'opacity 0.6s ease-in-out, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          >
            {formatDescription(state.sectionDescriptions[section.id])}
          </div>
        ))}
      </div>
      
      {/* Titles container below */}
      <div className="section-titles-container">
        {state.sections.map(section => (
          <div 
            key={`title-${section.id}`}
            className={`section-title ${shouldShowSection(section) ? 'active' : ''}`}
            style={{ 
              opacity: getTitleOpacity(section),
              transform: shouldShowSection(section) ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s ease-in-out, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          >
            {section.title}
          </div>
        ))}
      </div>
    </>
  )
} 